import moment, { Moment } from 'moment/moment';
import { ASAP } from '../../constants/order';

enum UserAgent {
  Chrome,
  Safari,
  Firefox,
  Unknown,
}

const getAgent = () => {
  const chromeAgent = navigator.userAgent.indexOf('Chrome') > -1;
  const safariAgent = navigator.userAgent.indexOf('Safari') > -1;
  const firefoxAgent = navigator.userAgent.indexOf('Firefox') > -1;

  switch (true) {
    case chromeAgent && safariAgent:
      return UserAgent.Chrome;
    case safariAgent:
      return UserAgent.Safari;
    case firefoxAgent:
      return UserAgent.Firefox;
    default:
      return UserAgent.Unknown;
  }
};

const getOffset = () => {
  switch (getAgent()) {
    case UserAgent.Safari:
      return 0;
    case UserAgent.Chrome:
      return 3;
    case UserAgent.Firefox:
      return 3;
    default:
      return 0;
  }
};

export const renderDate = (
  date: string | Date,
  forPromotionSchedule?: boolean,
  withoutOffset?: boolean
) => {
  const newDate = new Date(date);
  const hours =
    newDate.getHours() +
    (forPromotionSchedule || withoutOffset ? 0 : getOffset());

  const minutes = newDate.getMinutes();
  const h = hours < 10 ? `0${hours}` : '' + hours;
  const m = minutes < 10 ? `0${minutes}` : '' + minutes;

  return forPromotionSchedule
    ? `${h}:${m}:00`
    : `${new Date(date).toLocaleDateString('ru-RU')} ${h}:${m}`;
};

export const getIntFromString = (string: string, canBeEmpty?: boolean) => {
  return !isNaN(+string) && !string.includes('-') && string !== ''
    ? parseInt(string)
    : 0;
};

export const getZeroOrString = (string: string, canBeEmpty?: boolean) => {
  if (canBeEmpty && string === '') return '';

  return !isNaN(+string) ? string : 0;
};

export const copyObject = (object: {}) => JSON.parse(JSON.stringify(object));

export const getFilenameFromHeader = (
  contentDisposition?: string,
  contentType?: string
) => {
  let filename = 'file';

  if (contentDisposition) {
    // Попробовать filename*= (UTF-8)
    const utf8Matches = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(
      contentDisposition
    );
    if (utf8Matches && utf8Matches[1]) {
      try {
        filename = decodeURIComponent(utf8Matches[1]);
        return filename;
      } catch (e) {
        console.warn('Ошибка декодирования filename*', e);
      }
    }

    // fallback на обычный filename=
    const fallbackMatches = /filename\s*=\s*['"]?([^'";]+)['"]?/i.exec(
      contentDisposition
    );
    if (fallbackMatches && fallbackMatches[1]) {
      filename = fallbackMatches[1];
      return filename;
    }
  }

  // Если имени нет — подставляем расширение по Content-Type
  if (
    contentType?.includes('excel') ||
    contentType?.includes('spreadsheetml')
  ) {
    filename += '.xlsx';
  }

  return filename;
};

export const downloadFile = (blob: Blob, filename?: string) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  if (filename) a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

export const getPassedTime = (
  createdAt: string,
  format = 'DD.MM.YYYY HH:mm'
) => {
  const now = moment();
  const createdDate = moment(createdAt);

  const days = moment()
    .startOf('day')
    .diff(moment(createdAt).startOf('day'), 'days');
  const minutes = now.diff(createdDate, 'minutes');
  const hours = now.diff(createdDate, 'hours');

  if (minutes <= 1) return 'только что';
  if (hours < 1) return `${minutes} мин назад`;

  const time = createdDate.format('HH:mm');

  if (days === 0) return `сегодня в ${time}`;
  if (days === 1) return `вчера в ${time}`;

  return createdDate.format(format);
};

const adaptReadyTime = (time: string) => {
  const date = moment(time);
  return `${date.format('DD.MM')} к ${date.format('HH:mm')}`;
};

const getTimeLeft = (time: string) => {
  const hour = 1000 * 60 * 60;
  if (Math.abs(moment().diff(moment(time))) > hour) {
    return adaptReadyTime(time);
  }
  const now = moment();
  const orderTime = moment(time);
  const diffMS = orderTime.diff(now);
  const m = moment.duration(diffMS);
  if (diffMS > 0) {
    const minutes = Math.floor(m.asMinutes());
    const hours = Math.floor(minutes / 60);
    const minutesLeft = minutes - hours * 60;

    return `через ${hours}:${
      minutesLeft < 10 ? '0' + String(minutesLeft) : minutesLeft
    }`;
  } else {
    const minutes = Math.floor(m.asMinutes());
    const hours = Math.floor(minutes / 60);
    const minutesLeft = minutes - hours * 60;

    return `${Math.abs(hours)}:${
      minutesLeft < 10 ? '0' + String(minutesLeft) : minutesLeft
    } назад`;
  }
};

export const getDeliveryTime = (
  readyTime: string,
  waitingTime: string | null
) => {
  const date = moment(readyTime).format('DD.MM');
  const isDateInTime = waitingTime?.includes(date);

  return waitingTime
    ? (readyTime && !isDateInTime ? date + ' ' : '') + waitingTime
    : readyTime
    ? getTimeLeft(readyTime)
    : ASAP;
};

export const getUserName = (
  user:
    | {
        lastName: string;
        firstName: string;
        patronymic: string;
      }
    | undefined,
  showFull = false
) => {
  const lastName = user?.lastName;
  const firstName = user?.firstName;
  const patronymic = user?.patronymic;

  if (showFull) {
    return [lastName, firstName, patronymic].filter(Boolean).join(' ');
  }

  return `${lastName} ${firstName?.slice(0, 1)}.${patronymic?.slice(0, 1)}`;
};

export const timeMask = 'YYYY-MM-DDTHH:mm:ss';

export const stringToDate = (time: string | null) => {
  if (!time) return '';
  return moment(time, timeMask).toDate();
};

export const dateToString = (
  time: Date | string | null,
  mask: string = timeMask,
  keepError = false
) => {
  if (!time) return '';
  const formatted = moment(time).format(mask);
  if (formatted === 'Invalid date') return keepError ? 'Invalid date' : '';
  return formatted;
};
